import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CircleAlert,
  Copy,
  KeyRound,
  Plus,
  Scale,
  Server,
  ServerCrash,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router';

import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Drawer } from '../components/Drawer';
import { EmptyState } from '../components/EmptyState';
import { Field, inputClass } from '../components/Field';
import { FormProblem } from '../components/FormProblem';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { useToast } from '../components/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import { Occupancy } from '../features/platform/Occupancy';
import { kindLabels } from '../features/inventory/api';
import {
  clock,
  createMediaNode,
  equipmentKey,
  failoverBlockLabels,
  listEquipment,
  listMediaNodes,
  mediaNodesKey,
  moveEquipment,
  noMediaNodeProblem,
  numericProblems,
  plural,
  previewRebalance,
  rebalanceKey,
  rebalanceRoutes,
  rotateMediaNodeKey,
  runRebalance,
  statusLabels,
  toMediaNodeProblem,
  updateMediaNode,
  type Equipment,
  type FailoverRun,
  type MediaNode,
  type MediaNodeInput,
  type MediaNodeList,
  type MediaNodeProblem,
  type PendingFailover,
  type RebalanceMove,
} from '../features/platform/mediaNodes';
import { copyText } from '../lib/clipboard';
import { useFocusTrap } from '../lib/useFocusTrap';

const statusTone = { active: 'ok', paused: 'warn', disabled: 'neutral' } as const;

/**
 * The platform's media nodes (ADR-048): register, edit, pause, rekey and move
 * equipment between them. Super administrators only. A node key is shown once,
 * held only in component state, and dropped when its dialog closes.
 */
export function MediaNodesPage() {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<MediaNode | 'new' | null>(null);
  const [shownKey, setShownKey] = useState<{ node: MediaNode; key: string } | null>(null);
  const [rekeying, setRekeying] = useState<MediaNode | null>(null);
  const [disabling, setDisabling] = useState<MediaNode | null>(null);
  const [equipmentOf, setEquipmentOf] = useState<MediaNode | null>(null);
  const [rebalancing, setRebalancing] = useState(false);

  // Every 15 s, the period of the health checks: health and failover follow.
  const nodes = useQuery({
    queryKey: mediaNodesKey,
    queryFn: () => listMediaNodes(request),
    refetchInterval: 15_000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: mediaNodesKey });

  const status = useMutation({
    mutationFn: ({ n, to }: { n: MediaNode; to: MediaNode['status'] }) =>
      updateMediaNode(request, n.id, { status: to }),
    onSuccess: async (n) => {
      setDisabling(null);
      await refresh();
      notify(`${n.name}: ${statusLabels[n.status].toLowerCase()}.`);
    },
    onError: (err) => {
      setDisabling(null);
      notify(toMediaNodeProblem(err).form ?? 'Não foi possível mudar a situação.', 'error');
    },
  });
  const rekey = useMutation({
    mutationFn: (n: MediaNode) => rotateMediaNodeKey(request, n.id),
    // The key must not linger in the mutation cache.
    gcTime: 0,
    onSuccess: (r: { key: string }, n: MediaNode) => {
      setRekeying(null);
      setShownKey({ node: n, key: r.key });
      // Drop the key from the mutation state as soon as it is in the dialog.
      rekey.reset();
    },
    onError: (err) => {
      setRekeying(null);
      notify(toMediaNodeProblem(err).form ?? 'Não foi possível trocar a chave.', 'error');
    },
  });

  const list = nodes.data?.items ?? [];
  const balanceable = list.filter((n) => n.status === 'active' && n.up).length >= 2;
  return (
    <>
      <PageHeader
        title="Máquinas de vídeo"
        description="Servidores de vídeo da instalação. Cada equipamento (gravador com seus canais, ou câmera avulsa) fica numa máquina."
        actions={
          <div className="flex gap-2">
            <Button
              disabled={!balanceable}
              title={
                balanceable
                  ? 'Distribui os equipamentos entre as máquinas ativas e disponíveis, com prévia.'
                  : 'Precisa de pelo menos duas máquinas ativas e disponíveis.'
              }
              onClick={() => {
                setRebalancing(true);
              }}
            >
              <Scale aria-hidden="true" className="size-4" strokeWidth={2} />
              Rebalancear
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setEditing('new');
              }}
            >
              <Plus aria-hidden="true" className="size-4" strokeWidth={2} />
              Nova máquina
            </Button>
          </div>
        }
      />
      <div className="space-y-3 p-4 sm:p-6">
        {nodes.data && <FailoverBanner list={nodes.data} />}
        <div className="rounded-lg border border-line bg-surface">
          {nodes.isError ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar as máquinas"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void nodes.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : nodes.data && list.length === 0 ? (
            <EmptyState
              icon={Server}
              title="Nenhuma máquina cadastrada"
              description="Cadastre a primeira máquina de vídeo: sem ela, nenhum equipamento novo pode ser cadastrado."
            />
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[960px] text-left text-[13px]"
                aria-busy={nodes.isFetching}
              >
                <caption className="sr-only">Máquinas de vídeo</caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    {['Máquina', 'Situação', 'Saúde', 'Ocupação', 'Endereço público'].map((h) => (
                      <th key={h} scope="col" className="px-4 py-2 font-medium">
                        {h}
                      </th>
                    ))}
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!nodes.data
                    ? Array.from({ length: 3 }, (_, i) => (
                        <tr
                          key={i}
                          aria-hidden="true"
                          className="border-b border-line last:border-0"
                        >
                          {['w-40', 'w-14', 'w-20', 'w-24', 'w-36', 'w-48'].map((w) => (
                            <td key={w} className="px-4 py-3.5">
                              <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : list.map((n) => (
                        <tr
                          key={n.id}
                          className="border-b border-line last:border-0 hover:bg-surface-hover/50"
                        >
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2 font-medium text-fg">
                              <Link
                                to={`/media-nodes/${encodeURIComponent(n.id)}`}
                                className="underline-offset-2 hover:underline"
                              >
                                {n.name}
                              </Link>
                              {n.local && (
                                <span className="rounded bg-accent-soft px-1.5 py-0.5 text-2xs font-medium text-accent">
                                  Local
                                </span>
                              )}
                            </div>
                            <div className="font-mono text-2xs text-fg-subtle">{n.engine_url}</div>
                          </td>
                          <td className="px-4 py-2.5">
                            <StatusBadge tone={statusTone[n.status]}>
                              {statusLabels[n.status]}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-2.5">
                            <StatusBadge tone={n.up ? 'ok' : 'warn'}>
                              {n.up ? 'Disponível' : 'Indisponível'}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-2.5">
                            <Occupancy node={n} />
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">
                            {`${n.public_host}:${String(n.rtsp_port)}`}
                            {n.replay_port > 0 && (
                              <div className="text-2xs text-fg-subtle">{`ONVIF ${String(n.replay_port)}`}</div>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex justify-end gap-1 whitespace-nowrap">
                              <Button
                                variant="ghost"
                                className="h-7"
                                onClick={() => {
                                  setEditing(n);
                                }}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                className="h-7"
                                onClick={() => {
                                  setEquipmentOf(n);
                                }}
                              >
                                Equipamentos
                              </Button>
                              {n.status === 'active' ? (
                                <Button
                                  variant="ghost"
                                  className="h-7"
                                  disabled={status.isPending}
                                  title="Não recebe equipamentos novos; mantém os atuais."
                                  onClick={() => {
                                    status.mutate({ n, to: 'paused' });
                                  }}
                                >
                                  Pausar
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  className="h-7"
                                  disabled={status.isPending}
                                  onClick={() => {
                                    status.mutate({ n, to: 'active' });
                                  }}
                                >
                                  Reativar
                                </Button>
                              )}
                              {n.status !== 'disabled' && (
                                <Button
                                  variant="ghost"
                                  className="h-7"
                                  onClick={() => {
                                    setDisabling(n);
                                  }}
                                >
                                  Desativar
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                className="h-7"
                                onClick={() => {
                                  setRekeying(n);
                                }}
                              >
                                <KeyRound aria-hidden="true" className="size-3.5" />
                                Trocar chave
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <p className="text-xs text-fg-subtle">
          Saúde: a máquina fica indisponível depois de 3 verificações seguidas sem resposta (a cada
          15 s); 2 minutos depois, os equipamentos dela vão para as outras. Ocupação: câmera
          gravando pesa 10; só ao vivo, 1. Equipamentos novos só vão para máquina ativa e disponível
          que fique em até 70% (a marca na barra).
        </p>
      </div>

      {editing && (
        <NodeDrawer
          node={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={async (n, key) => {
            setEditing(null);
            if (key) setShownKey({ node: n, key });
            else notify(`${n.name} salva.`);
            await refresh();
          }}
        />
      )}
      {equipmentOf && (
        <EquipmentDrawer
          node={equipmentOf}
          nodes={list}
          onClose={() => {
            setEquipmentOf(null);
          }}
          onMoved={refresh}
        />
      )}
      {rebalancing && (
        <RebalanceDialog
          onClose={() => {
            setRebalancing(false);
          }}
          onDone={refresh}
        />
      )}
      {shownKey && (
        <KeyDialog
          node={shownKey.node}
          secret={shownKey.key}
          onClose={() => {
            setShownKey(null);
          }}
        />
      )}
      {rekeying && (
        <ConfirmDialog
          title={`Trocar a chave de ${rekeying.name}?`}
          confirmLabel="Trocar a chave"
          busy={rekey.isPending}
          onConfirm={() => {
            rekey.mutate(rekeying);
          }}
          onCancel={() => {
            setRekeying(null);
          }}
        >
          {rekeying.local ? (
            <>
              <p>
                A chave atual para de valer na hora, e o agente fica sem conexão até receber a nova
                e ser reiniciado.
              </p>
              <p className="mt-2 text-fg">
                Na máquina local, a troca vale para o agente; atualize a MEDIA_NODE_KEY no .env
                dele.
              </p>
            </>
          ) : (
            <p>
              A chave atual para de valer na hora. A máquina fica indisponível até o{' '}
              <code className="font-mono">.env</code> dela receber a nova e o servidor de vídeo e o
              agente serem reiniciados.
            </p>
          )}
        </ConfirmDialog>
      )}
      {disabling && (
        <ConfirmDialog
          title={`Desativar ${disabling.name}?`}
          confirmLabel="Desativar"
          busy={status.isPending}
          onConfirm={() => {
            status.mutate({ n: disabling, to: 'disabled' });
          }}
          onCancel={() => {
            setDisabling(null);
          }}
        >
          {disabling.usage.equipment === 0
            ? 'A máquina não tem equipamentos e deixa de receber novos até ser reativada.'
            : `As câmeras ${disabling.usage.equipment === 1 ? 'do equipamento' : `dos ${String(disabling.usage.equipment)} equipamentos`} desta máquina ficam indisponíveis até ela ser reativada ou eles serem movidos. Para só parar de receber equipamentos novos, use Pausar.`}
        </ConfirmDialog>
      )}
    </>
  );
}

/** How long the latest failover stays on the page. */
const lastShownFor = 24 * 60 * 60_000;

/**
 * The failover (ADR-050): each node down, how long, and the time until its
 * equipment moves (or what blocks it); then the latest result for a day.
 * Times follow the server's clock: the offset to the browser's is taken once
 * per answer, and the browser only advances it.
 */
function FailoverBanner({ list }: { list: MediaNodeList }) {
  const { failover, receivedAt } = list;
  const [now, setNow] = useState(() => Date.now());
  const ticking = failover.pending.length > 0;
  useEffect(() => {
    if (!ticking) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [ticking]);
  const answeredAt = Date.parse(failover.now);
  const offset = answeredAt - receivedAt;
  // Never before the answer: `now` may predate a list that just arrived.
  const serverNow = Math.max(answeredAt, now + offset);
  const last = failover.last;
  const showLast = last !== null && serverNow - Date.parse(last.finished_at) < lastShownFor;
  if (!ticking && !showLast) return null;
  return (
    <div className="space-y-2">
      {failover.pending.map((p) => (
        <PendingLine key={p.node_id} p={p} serverNow={serverNow} />
      ))}
      {last !== null && showLast && <LastFailover run={last} />}
    </div>
  );
}

/** One node down: since when, and the countdown, the move under way or what stops it. */
function PendingLine({ p, serverNow }: { p: PendingFailover; serverNow: number }) {
  const down = `Máquina ${p.node_name} fora há ${clock(serverNow - Date.parse(p.down_since))}`;
  const equipment = plural(p.equipment, 'equipamento', 'equipamentos');
  const blocked = p.blocked ? failoverBlockLabels[p.blocked] : null;
  const left = Date.parse(p.moves_at) - serverNow;
  const text = blocked
    ? `${down}. ${blocked}.`
    : p.no_target
      ? `${down}; nenhuma máquina ativa e disponível para receber ${equipment}.`
      : left > 0
        ? `${down}; troca em ${clock(left)}.`
        : `${down}; movendo ${equipment}.`;
  const tone =
    blocked || p.no_target
      ? 'border-crit/30 bg-crit-soft text-crit'
      : 'border-warn/30 bg-warn-soft text-warn';
  // Not a live region: its text changes every second.
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-[13px] ${tone}`}>
      <ServerCrash aria-hidden="true" className="mt-0.5 size-4 shrink-0" strokeWidth={2} />
      <span className="tabular-nums">{text}</span>
    </div>
  );
}

/** The latest failover: how many moved, where to, and who passed 100%. */
function LastFailover({ run }: { run: FailoverRun }) {
  const { session } = useAuth();
  const when = new Date(run.finished_at).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: session?.tenant.timezone,
  });
  const targets = Object.entries(run.targets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, n]) => `${name} (${String(n)})`)
    .join(', ');
  const left = plural(run.left, 'equipamento ficou', 'equipamentos ficaram');
  const outcome =
    run.outcome === 'aborted'
      ? ` Interrompida: a máquina voltou; ${left} nela.`
      : run.outcome === 'no_target'
        ? ` Parou sem máquina para receber; ${left} nela.`
        : '';
  const over =
    run.over_capacity.length > 0
      ? ` Acima da capacidade: ${run.over_capacity.join(', ')}. Aumente a capacidade ou use Rebalancear quando a máquina voltar.`
      : '';
  const tone =
    run.over_capacity.length > 0
      ? 'border-crit/30 bg-crit-soft text-crit'
      : 'border-line bg-surface text-fg-muted';
  return (
    <div role="status" className={`rounded-md border px-3 py-2 text-[13px] ${tone}`}>
      {`Última troca automática, ${run.node_name} (${when}): ${plural(run.moved, 'equipamento movido', 'equipamentos movidos')}${targets ? ` para ${targets}` : ''}.${outcome}${over}`}
    </div>
  );
}

/**
 * Rebalance (ADR-050): the preview, grouped by route ("N equipamentos: de X
 * para Y"), and the confirmation that runs exactly those moves. A preview
 * gone stale is fetched again, with a note. Cancel takes the focus first.
 */
function RebalanceDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { request } = useAuth();
  const { notify } = useToast();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const [problem, setProblem] = useState<string | null>(null);
  // A preview fetched again when the window regains focus could differ from
  // what the operator is reading: it changes only on an answer from the run.
  const preview = useQuery({
    queryKey: rebalanceKey,
    queryFn: () => previewRebalance(request),
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const run = useMutation({
    mutationFn: (moves: RebalanceMove[]) => runRebalance(request, moves),
    onMutate: () => {
      setProblem(null);
    },
    onSuccess: async (r) => {
      notify(
        `Rebalanceamento concluído: ${plural(r.moved, 'equipamento movido', 'equipamentos movidos')}.`,
      );
      await onDone();
      onClose();
    },
    onError: async (err) => {
      setProblem(toMediaNodeProblem(err).form ?? 'Não foi possível rebalancear.');
      // rebalanceKey is under mediaNodesKey: this refreshes the list and the preview.
      await onDone();
    },
  });
  // While the moves run, Escape, the backdrop and Cancel do nothing: the
  // dialog closes with the result.
  const running = run.isPending;
  const close = useCallback(() => {
    if (!running) onClose();
  }, [running, onClose]);
  useFocusTrap(ref, close);
  // While the moves run both buttons are disabled; a disabled button loses
  // the focus to the page behind, so the dialog takes it. When they end
  // with the dialog still open (an error), the focus goes to Cancel: on the
  // dialog itself, Shift+Tab would leave it.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (running) root.focus();
    else if (document.activeElement === root)
      root.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, [running]);
  const moves = preview.data ?? [];
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={close} />
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        aria-busy={preview.isFetching || run.isPending}
        tabIndex={-1}
        className="relative w-full max-w-md rounded-lg border border-line-strong bg-surface-raised p-5 shadow-2xl shadow-black/50 outline-none"
      >
        <div className="flex gap-3">
          <div className="grid size-8 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
            <Scale aria-hidden="true" className="size-4" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-sm font-semibold text-fg">
              Rebalancear as máquinas
            </h2>
            <div id={bodyId} className="mt-2 space-y-3 text-[13px] leading-relaxed text-fg-muted">
              <FormProblem message={problem} />
              {preview.isError ? (
                <p className="text-crit">
                  Não foi possível calcular a prévia. Feche e tente de novo.
                </p>
              ) : !preview.data ? (
                <p>Calculando a prévia…</p>
              ) : moves.length === 0 ? (
                <p>As máquinas ativas e disponíveis já estão equilibradas: nada a mover.</p>
              ) : (
                <>
                  <ul className="space-y-1 text-fg">
                    {rebalanceRoutes(moves).map((r) => (
                      <li key={`${r.from}>${r.to}`}>
                        {`${plural(r.count, 'equipamento', 'equipamentos')}: de ${r.from} para ${r.to}`}
                      </li>
                    ))}
                  </ul>
                  <p>
                    Cada equipamento movido tem o ao vivo e a gravação encerrados na máquina de
                    origem e reabertos na nova em segundos.
                  </p>
                  <details>
                    <summary className="cursor-pointer text-xs text-fg-subtle">
                      Ver os equipamentos
                    </summary>
                    <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto text-xs">
                      {moves.map((m) => (
                        <li key={m.device_id}>
                          {`${m.name} (${m.tenant_name}, peso ${String(m.weight)}): ${m.from.name} → ${m.to.name}`}
                        </li>
                      ))}
                    </ul>
                  </details>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={close} disabled={running} data-autofocus>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={moves.length === 0 || run.isPending || preview.isFetching}
            onClick={() => {
              run.mutate(moves);
            }}
          >
            {run.isPending
              ? 'Aguarde…'
              : `Mover ${plural(moves.length, 'equipamento', 'equipamentos')}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Shows a node key once, with the MEDIA_NODE_ID beside it. Escape and the
 * backdrop do nothing: the key goes away only on an explicit "Já copiei".
 */
function KeyDialog({
  node,
  secret,
  onClose,
}: {
  node: MediaNode;
  secret: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  useFocusTrap(ref, () => undefined);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" />
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="relative w-full max-w-lg rounded-lg border border-line-strong bg-surface-raised p-5 shadow-2xl shadow-black/50"
      >
        <div className="flex gap-3">
          <div className="grid size-8 shrink-0 place-items-center rounded-full bg-warn-soft text-warn">
            <KeyRound aria-hidden="true" className="size-4" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-sm font-semibold text-fg">
              {`Chave da máquina ${node.name}`}
            </h2>
            <div
              id={bodyId}
              className="mt-2 flex items-start gap-2 rounded-md border border-warn/30 bg-warn-soft px-3 py-2 text-[13px] text-warn"
            >
              <TriangleAlert
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0"
                strokeWidth={2}
              />
              <span>
                Copie agora: a chave não será mostrada de novo. Se ela se perder, troque a chave.
              </span>
            </div>
            <p className="mt-3 text-[13px] text-fg-muted">
              {node.local
                ? 'Na máquina local, a troca vale para o agente; atualize a MEDIA_NODE_KEY no .env dele.'
                : 'No .env da máquina de vídeo, use:'}
            </p>
            <dl className="mt-3 space-y-3">
              <Secret label="MEDIA_NODE_ID" value={node.id} />
              <Secret label="MEDIA_NODE_KEY" value={secret} autoFocus />
            </dl>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button variant="primary" onClick={onClose}>
            Já copiei
          </Button>
        </div>
      </div>
    </div>
  );
}

function Secret({
  label,
  value,
  autoFocus = false,
}: {
  label: string;
  value: string;
  /** Receives the dialog's initial focus. */
  autoFocus?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <dt className="text-2xs font-medium tracking-wide text-fg-subtle uppercase">{label}</dt>
      <dd className="mt-1 flex items-center gap-2 rounded-md border border-line bg-canvas px-2.5 py-1.5">
        <code className="min-w-0 flex-1 font-mono text-xs break-all text-fg">{value}</code>
        <button
          type="button"
          aria-label={`Copiar ${label}`}
          {...(autoFocus ? { 'data-autofocus': true } : {})}
          onClick={() => {
            void copyText(value).then((ok) => {
              if (!ok) return;
              setCopied(true);
              setTimeout(() => {
                setCopied(false);
              }, 1500);
            });
          }}
          className="grid size-7 shrink-0 place-items-center rounded text-fg-subtle hover:bg-surface-hover hover:text-fg"
        >
          {copied ? (
            <Check aria-hidden="true" className="size-3.5" />
          ) : (
            <Copy aria-hidden="true" className="size-3.5" />
          )}
        </button>
      </dd>
    </div>
  );
}

interface FormState {
  name: string;
  engine_url: string;
  agent_url: string;
  snap_pump_url: string;
  public_host: string;
  rtsp_port: string;
  replay_port: string;
  webrtc_port: string;
  capacity: string;
}

const emptyForm: FormState = {
  name: '',
  engine_url: '',
  agent_url: '',
  snap_pump_url: '',
  public_host: '',
  rtsp_port: '28555',
  replay_port: '28556',
  webrtc_port: '28000',
  capacity: '1000',
};

function formOf(n: MediaNode | null): FormState {
  if (!n) return emptyForm;
  return {
    name: n.name,
    engine_url: n.engine_url,
    agent_url: n.agent_url,
    snap_pump_url: n.snap_pump_url,
    public_host: n.public_host,
    rtsp_port: String(n.rtsp_port),
    replay_port: String(n.replay_port),
    webrtc_port: String(n.webrtc_port),
    capacity: String(n.capacity),
  };
}

function inputOf(f: FormState): MediaNodeInput {
  return {
    name: f.name.trim(),
    engine_url: f.engine_url.trim(),
    agent_url: f.agent_url.trim(),
    snap_pump_url: f.snap_pump_url.trim(),
    public_host: f.public_host.trim(),
    rtsp_port: Number(f.rtsp_port),
    replay_port: Number(f.replay_port),
    webrtc_port: Number(f.webrtc_port),
    capacity: Number(f.capacity),
  };
}

interface FormField {
  key: keyof FormState;
  label: string;
  hint?: string;
  numeric?: boolean;
  mono?: boolean;
}

const addressFields: FormField[] = [
  {
    key: 'engine_url',
    label: 'API do servidor de vídeo (túnel)',
    hint: 'Ex.: http://10.8.0.3:28081',
    mono: true,
  },
  {
    key: 'agent_url',
    label: 'Agente (túnel)',
    hint: 'Ex.: http://10.8.0.3:29100. Vazio: sem agente.',
    mono: true,
  },
  {
    key: 'snap_pump_url',
    label: 'Snap-pump (túnel)',
    hint: 'Ex.: http://10.8.0.3:29000. Vazio: sem snap-pump.',
    mono: true,
  },
  {
    key: 'public_host',
    label: 'Endereço público',
    hint: 'Nome ou IP que câmeras e integrações alcançam.',
    mono: true,
  },
];

const portFields: FormField[] = [
  { key: 'rtsp_port', label: 'Porta RTSP', numeric: true },
  {
    key: 'replay_port',
    label: 'Porta da reprodução ONVIF',
    hint: '0: sem reprodução ONVIF.',
    numeric: true,
  },
  { key: 'webrtc_port', label: 'Porta WebRTC', numeric: true },
  {
    key: 'capacity',
    label: 'Capacidade (peso)',
    hint: 'Câmera gravando pesa 10; só ao vivo, 1.',
    numeric: true,
  },
];

function NodeDrawer({
  node,
  onClose,
  onSaved,
}: {
  node: MediaNode | null;
  onClose: () => void;
  onSaved: (n: MediaNode, key?: string) => Promise<void>;
}) {
  const { request } = useAuth();
  const [form, setForm] = useState<FormState>(formOf(node));
  const [problem, setProblem] = useState<MediaNodeProblem>(noMediaNodeProblem);
  const save = useMutation({
    mutationFn: async (): Promise<{ node: MediaNode; key?: string }> => {
      if (node) return { node: await updateMediaNode(request, node.id, inputOf(form)) };
      return createMediaNode(request, inputOf(form));
    },
    // A new node's key must not linger in the mutation cache.
    gcTime: 0,
    onMutate: () => {
      setProblem(noMediaNodeProblem);
    },
    onSuccess: (r) => onSaved(r.node, r.key),
    onError: (err) => {
      setProblem(toMediaNodeProblem(err));
    },
  });
  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    const invalid = numericProblems(form);
    if (Object.keys(invalid).length > 0) {
      setProblem({ fields: invalid, form: null });
      return;
    }
    save.mutate();
  };
  const input = (f: FormField) => (
    <Field
      key={f.key}
      label={f.label}
      error={problem.fields[f.key]}
      {...(f.hint ? { hint: f.hint } : {})}
    >
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          autoComplete="off"
          spellCheck={false}
          className={`${inputClass} ${f.mono ? 'font-mono' : ''}`}
          inputMode={f.numeric ? 'numeric' : undefined}
          value={form[f.key]}
          onChange={(e) => {
            setForm({ ...form, [f.key]: e.target.value });
          }}
        />
      )}
    </Field>
  );
  return (
    <Drawer
      title={node ? `Editar ${node.name}` : 'Nova máquina de vídeo'}
      {...(node ? {} : { description: 'A chave da máquina aparece uma única vez, ao cadastrar.' })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" form="media-node-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : node ? 'Salvar' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      <form id="media-node-form" className="space-y-5" onSubmit={submit} noValidate>
        <FormProblem message={problem.form} />
        {input({ key: 'name', label: 'Nome' })}
        <fieldset className="space-y-4">
          <legend className="mb-3 text-2xs font-medium tracking-wide text-fg-subtle uppercase">
            Endereços
          </legend>
          {addressFields.map(input)}
        </fieldset>
        <fieldset>
          <legend className="mb-3 text-2xs font-medium tracking-wide text-fg-subtle uppercase">
            Portas e capacidade
          </legend>
          <div className="grid grid-cols-2 gap-4">{portFields.map(input)}</div>
        </fieldset>
        {node?.local && (
          <p className="text-xs text-fg-subtle">
            Máquina local: criada a partir do .env do servidor central. O que for salvo aqui passa a
            valer no lugar dele.
          </p>
        )}
      </form>
    </Drawer>
  );
}

function EquipmentDrawer({
  node,
  nodes,
  onClose,
  onMoved,
}: {
  node: MediaNode;
  nodes: MediaNode[];
  onClose: () => void;
  onMoved: () => Promise<void>;
}) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const targets = nodes.filter((n) => n.id !== node.id && n.status === 'active');
  const [target, setTarget] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<{ e: Equipment; to: MediaNode } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const items = useQuery({
    queryKey: equipmentKey(node.id),
    queryFn: () => listEquipment(request, node.id),
  });
  const move = useMutation({
    mutationFn: ({ e, to }: { e: Equipment; to: MediaNode }) =>
      moveEquipment(request, to.id, e.device_id),
    onMutate: () => {
      setProblem(null);
    },
    onSuccess: async (_r, { e, to }) => {
      setConfirm(null);
      notify(`${e.name} movido para ${to.name}.`);
      await Promise.all([qc.invalidateQueries({ queryKey: equipmentKey(node.id) }), onMoved()]);
    },
    onError: async (err) => {
      setConfirm(null);
      setProblem(toMediaNodeProblem(err).form ?? 'Não foi possível mover o equipamento.');
      await Promise.all([qc.invalidateQueries({ queryKey: equipmentKey(node.id) }), onMoved()]);
    },
  });
  const data = items.data ?? [];
  return (
    <Drawer
      title={`Equipamentos de ${node.name}`}
      description="Mover encerra o ao vivo e as gravações do equipamento aqui e os recomeça na outra máquina."
      onClose={onClose}
    >
      <div className="space-y-4">
        <FormProblem message={problem} />
        {items.isError ? (
          <EmptyState
            tone="crit"
            icon={CircleAlert}
            title="Não foi possível carregar os equipamentos"
            description="Verifique a conexão com o servidor e tente novamente."
            action={
              <Button variant="primary" onClick={() => void items.refetch()}>
                Tentar novamente
              </Button>
            }
          />
        ) : items.data && data.length === 0 ? (
          <EmptyState
            icon={Server}
            title="Nenhum equipamento nesta máquina"
            description="Equipamentos novos vão para a máquina ativa com mais folga."
          />
        ) : (
          <>
            {items.data && targets.length === 0 && (
              <p className="rounded-md border border-line bg-surface-raised px-3 py-2 text-xs text-fg-muted">
                Não há outra máquina ativa para receber equipamentos.
              </p>
            )}
            <table className="w-full text-left text-[13px]" aria-busy={items.isFetching}>
              <caption className="sr-only">{`Equipamentos de ${node.name}`}</caption>
              <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                <tr className="border-b border-line">
                  <th scope="col" className="py-2 pr-2 font-medium">
                    Equipamento
                  </th>
                  <th scope="col" className="px-2 py-2 text-right font-medium">
                    Peso
                  </th>
                  <th scope="col" className="py-2 pl-2 font-medium">
                    Mover para
                  </th>
                </tr>
              </thead>
              <tbody>
                {!items.data
                  ? Array.from({ length: 3 }, (_, i) => (
                      <tr key={i} aria-hidden="true" className="border-b border-line last:border-0">
                        {['w-32', 'ml-auto w-6', 'w-28'].map((w) => (
                          <td key={w} className="px-2 py-3.5">
                            <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                          </td>
                        ))}
                      </tr>
                    ))
                  : data.map((e) => {
                      const to = targets.find((n) => n.id === target[e.device_id]);
                      return (
                        <tr key={e.device_id} className="border-b border-line last:border-0">
                          <td className="py-2.5 pr-2 align-top">
                            <div className="text-fg">{e.name}</div>
                            <div className="text-2xs text-fg-subtle">
                              {`${(kindLabels as Record<string, string | undefined>)[e.kind] ?? e.kind} · ${plural(e.cameras, 'câmera', 'câmeras')}`}
                            </div>
                            <div className="text-2xs text-fg-subtle">{`${e.tenant_name} · ${e.site_name}`}</div>
                          </td>
                          <td className="px-2 py-2.5 text-right align-top font-mono text-xs text-fg-muted tabular-nums">
                            {e.weight}
                          </td>
                          <td className="py-2.5 pl-2 align-top">
                            <div className="flex items-center gap-1.5">
                              <select
                                aria-label={`Mover ${e.name} para`}
                                className={`${inputClass} h-8 w-32`}
                                disabled={targets.length === 0}
                                value={target[e.device_id] ?? ''}
                                onChange={(ev) => {
                                  setTarget({ ...target, [e.device_id]: ev.target.value });
                                }}
                              >
                                <option value="">Escolha</option>
                                {targets.map((n) => (
                                  <option key={n.id} value={n.id}>
                                    {n.name}
                                  </option>
                                ))}
                              </select>
                              <Button
                                disabled={!to || move.isPending}
                                onClick={() => {
                                  if (to) setConfirm({ e, to });
                                }}
                              >
                                Mover
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </>
        )}
      </div>
      {confirm && (
        <ConfirmDialog
          title={`Mover ${confirm.e.name} para ${confirm.to.name}?`}
          confirmLabel="Mover"
          tone="default"
          busy={move.isPending}
          onConfirm={() => {
            move.mutate(confirm);
          }}
          onCancel={() => {
            setConfirm(null);
          }}
        >
          O ao vivo, as gravações e as sessões das integrações deste equipamento são encerrados
          nesta máquina e recomeçam na outra em segundos.
        </ConfirmDialog>
      )}
    </Drawer>
  );
}
